import { cn } from "@babackapp/ui/lib/utils";
import { Button, Column, Row } from "@react-email/components";
import { getThemeButtonStyle } from "../button/shared-button-styling";
import { useEmailThemeRadius } from "../../hooks/use-emails-theme-radius";
import { sectionToParent, useThemeContextualColors } from "../../hooks";
import { resolveBlockFont } from "../../utils/fonts";
import { tLayout } from "../../translations";
import { useReturnVariableValues } from "../../hooks/use-return-variable-values";
import type { ReturnDownloadLabelBlockProps } from "./types";

const FALLBACK = {
  label: "Download my label",
  bonDeRetour: "Download my return slip",
};

export function ReturnDownloadLabelEmailRenderer({
  block,
  formatter,
  theme,
  locale,
  defaultLocale,
  section,
  isEditor,
}: ReturnDownloadLabelBlockProps & { isEditor?: boolean }) {
  const alignment = block.alignment || "center";
  const font = resolveBlockFont(block.font, theme);
  const buttonRadius = useEmailThemeRadius(theme, "button");
  const colors = useThemeContextualColors(theme, sectionToParent(section));
  const t = tLayout(locale, defaultLocale);
  const { getLabelDownload } = useReturnVariableValues();
  const { labelUrl, hasLogisticLabel } = getLabelDownload();

  const buttonStyle = getThemeButtonStyle({
    variant: block.properties?.variant || "default",
    theme,
    textColor: colors.text,
  });

  // `tLayout` has no per-key fallback, so resolve with a hardcoded English default.
  const autoLabel = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  // Pick the override + default for the active case (logistic return slip vs label).
  const override = hasLogisticLabel
    ? block.content?.[locale]?.bonDeRetour ||
      block.content?.[defaultLocale]?.bonDeRetour
    : block.content?.[locale]?.label || block.content?.[defaultLocale]?.label;

  const defaultText = hasLogisticLabel
    ? autoLabel("downloadLabel.bonDeRetour", FALLBACK.bonDeRetour)
    : autoLabel("downloadLabel.label", FALLBACK.label);

  const text =
    override && override.length
      ? formatter
        ? formatter(override)
        : override
      : defaultText;

  // No label URL → nothing to download (mirrors the legacy `downloadLabelHref !== ""` guard).
  if (!labelUrl && !isEditor) return null;
  const href = labelUrl ?? "#";

  return (
    <Row>
      <Column
        className={cn({
          "text-left": alignment === "left",
          "text-center": alignment === "center",
          "text-right": alignment === "right",
        })}
      >
        <Button
          href={href}
          className={cn("inline-block mx-1 no-underline", buttonRadius)}
          style={{ ...buttonStyle, fontFamily: font } as React.CSSProperties}
        >
          <span dangerouslySetInnerHTML={{ __html: text }} />
        </Button>
      </Column>
    </Row>
  );
}
